const HocHead = dynamic(() => import("@/Components/HigherOrderComponent/HocHead"), { ssr: true });
import React from "react";
import Banner from "./Banner";
import dynamic from "next/dynamic";
const ServicesList = dynamic(() => import("./ServicesList"), { ssr: true });
const BuildSection = dynamic(() => import("@/Components/BuildSection"), { ssr: true });
const TechnologyList = dynamic(() => import("./TechnologyList"), { ssr: true });
const ServicesIndustry = dynamic(() => import("./ServicesIndustry"), { ssr: true });
const ServicesPartner = dynamic(() => import("./ServicesPartner"), { ssr: true });
const Review = dynamic(() => import("./Review"), { ssr: true });
const Faq = dynamic(() => import("../Home/Faq"), { ssr: true });
const RFPForm = dynamic(() => import("../Home/Quote"), { ssr: true });
import { FaqBlock, RFPFormBlock } from "../Home";

export interface BannerBlock {
  __component: "blocks.2025-banner-inner";
  id: number;
  title: string;
  sub_title: string;
  description: string;
  banner_animation: any;
  banner_animation_link: string;
  Cta: { href: string; label: string };
  bannerCard: {
    id: number;
    title: string;
    description: string;
    image: any;
    link: { href: string; label: string };
  }[];
}

export interface ServicesListBlock {
  __component: "blocks.2025-services";
  id: number;
  servicesList: ServiceCategory[];
}

export interface ServiceCategory {
  id: number;
  title: string;
  cards: ServiceCard[];
}

export interface ServiceCard {
  id: number;
  title: string | null;
  WhatWeOffer: WhatWeOffer;
  featureCards: FeatureCard[];
}

export interface WhatWeOffer {
  id: number;
  title: string;
  readMore?: any;
  link: WhatWeOfferLink[];
}

export interface WhatWeOfferLink {
  id: number;
  href: string;
  label: string;
}

export interface FeatureCard {
  id: number;
  title: string;
  sub_title: string;
  image: any;
  link: FeatureLink[];
}

export interface FeatureImage {
  id: number;
  name: string;
  url: string;
}

export interface FeatureLink {
  id: number;
  href: string;
  label: string;
}

interface ServicesPageProps {
  seo?: any;
  data?: any;
  isMobile?: boolean;
  slug?: string;
}

export interface BuildSectionBlock {
  __component: "blocks.2025-build-section";
  id: number;
  title: string;
  description: string;
  buttonLink: { href: string; label: string };
}

export interface TechnologyListBlock {
  __component: "blocks.2025-technology";
  id: number;
  title: string;
  sub_title: string;
  cards: TechnologyCard[];
}

export interface TechnologyCard {
  id: number;
  title: string;
  technologies: Technology[];
  images: any;
}

export interface Technology {
  id: number;
  language: string;
  Image: any;
}

export interface TechnologyImage {
  id: number;
  name: string;
  url: string;
}

interface LinkItem {
  id: number;
  href: string;
  label: string;
}

interface CardItem {
  id: number;
  title: string;
  description: string;
  image: any;
  link: LinkItem[];
}

export interface ServicesIndustryBlock {
  __component: "blocks.2025-transformation-card";
  id: number;
  title: string;
  sub_title: string;
  cards: CardItem[];
}

export interface ServicesPartnerBlock {
  __component: "blocks.2025-partner";
  id: number;
  isMobile?: any;
  title: string;
  sub_title: string;
  partnerCards: {
    id: number;
    title: string;
    description: string;
    image: any;
  };
}

export interface TestimonialBlock {
  __component: "blocks.2025-testimonials";
  id: number;
  title: string;
  sub_title: string;
  testimonialCards: testimonialCards[];
}

export interface testimonialCards {
  id: number;
  title: string;
  rating: number;
  description: string;
  author: string;
  author_position: string;
  image: any;
}

export type Block =
  | BannerBlock
  | ServicesListBlock
  | BuildSectionBlock
  | TechnologyListBlock
  | ServicesIndustryBlock
  | ServicesPartnerBlock
  | FaqBlock
  | RFPFormBlock
  | TestimonialBlock;

export interface ActionCardBlock {
  __component: "shared.2025-action-card";
  id: number;
  title: string;
  description: string;
  Link: { href: string; label: string }[];
}

const ServicesPage: React.FC<ServicesPageProps> = ({ seo, data, isMobile }) => {
  const blocks = data;

  // Extract banner cards data
  const bannerBlock = blocks?.find((block: any) => block?.__component === "blocks.2025-banner-inner") as BannerBlock | undefined;
  const bannerCards = bannerBlock?.bannerCard || [];

  return (
    <>
      {seo && <HocHead seo={seo} />}

      {blocks?.map((block: any, i: number) => {
        switch (block?.__component) {
          case "blocks.2025-banner-inner":
            return (
              <Banner key={i} data={block as BannerBlock} isMobile={isMobile} />
            );

          case "blocks.2025-services":
            return <ServicesList key={i} data={block as ServicesListBlock} bannerCards={bannerCards} isMobile={isMobile} />;

          case "shared.2025-action-card":
            return <BuildSection key={i} data={block as ActionCardBlock} />;

          case "blocks.2025-technology":
            return (
              <TechnologyList key={i} data={block as TechnologyListBlock} />
            );

          case "blocks.2025-transformation-card":
            return (
              <ServicesIndustry key={i} data={block as ServicesIndustryBlock} />
            );

          case "blocks.2025-partner":
            return (
              <ServicesPartner key={i} data={block as ServicesPartnerBlock} isMobile={isMobile} />
            );

          case "blocks.2025-testimonials":
            return (
              <Review
                key={i}
                data={block as TestimonialBlock}
              // isMobile={isMobile}
              />
            );

          case "blocks.2025-home-fa-qs":
            return <Faq key={i} data={block as FaqBlock} />;

          case "blocks.2025-home-rfp":
            return <RFPForm key={i} data={block as RFPFormBlock} />;

          default:
            return (
              <div
                key={i}
                className="text-red-500 p-4 bg-red-50 border border-red-200 rounded-md mt-4 max-w-[1500px] mx-auto"
              >
                Unknown block type: {block?.__component || "undefined"}
              </div>
            );
        }
      })}
    </>
  );
}

export default ServicesPage;
